import {
  createQuote,
  createHold,
  checkoutHold,
  captureHold,
  getConfirmation,
  getHold,
  type Quote,
  type Hold,
  type HoldView,
  type UnavailableReason,
  type BookingView,
  type GuestParty,
  type GuestPartyWritable,
  type AcceptedDocument,
} from '@/api/generated';
import type { PartyValue } from '@/lib/party';
import { posthog } from '@/lib/posthog';
import { noteServerDate } from '@/lib/serverClock';
import { analyticsAllowed } from '@/lib/consent';

// formatDate renders a Date as a YYYY-MM-DD string in LOCAL calendar terms.
// The booking API treats checkin/checkout as plain calendar dates (no time
// zone), so we must NOT use toISOString() — that shifts to UTC and can move the
// date across midnight. We format the local Y/M/D the user actually picked.
export function toApiDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

// nightsBetween returns the number of occupied nights for [checkin, checkout).
export function nightsBetween(checkin: Date, checkout: Date): number {
  const ms = checkout.getTime() - checkin.getTime();
  return Math.round(ms / (1000 * 60 * 60 * 24));
}

// formatMoney renders minor units in the given ISO-4217 currency.
export function formatMoney(minor: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
    }).format(minor / 100);
  } catch {
    // Unknown currency code — fall back to a plain number + code.
    return `${(minor / 100).toFixed(2)} ${currency}`;
  }
}

// reasonMessage maps a machine reason to guest-facing copy.
export function reasonMessage(reason: UnavailableReason | undefined | null): string {
  switch (reason) {
    case 'no_inventory':
      return 'Those dates are no longer available.';
    case 'no_rate':
      return 'Pricing is not available for those dates yet.';
    case 'min_stay':
      return 'Your stay is shorter than the minimum for those dates.';
    case 'max_stay':
      return 'Your stay is longer than the maximum for those dates.';
    case 'stop_sell':
      return 'Booking is closed for one or more of those nights.';
    case 'closed_to_arrival':
      return 'Arrival is not allowed on the selected check-in date.';
    case 'closed_to_departure':
      return 'Departure is not allowed on the selected check-out date.';
    case 'over_capacity':
      return 'Too many guests for this apartment.';
    default:
      return 'Those dates are not available.';
  }
}

// toGuestParty converts the picker's PartyValue (lib/party.ts, Task 12) into
// the wire shape POST /quote and POST /holds expect. Mirrors partyToSearch's
// own convention (same file): a child slot with no age yet answered is
// dropped rather than sent as e.g. `null`, and infants/children_ages are
// omitted entirely at zero/empty rather than sent as noise — callers gate
// on isPartyComplete before this is reachable in practice, but this stays
// defensive rather than throwing, same reasoning as partyToSearch.
function toGuestParty(party: PartyValue): GuestPartyWritable {
  const childrenAges = party.childrenAges.filter((age): age is number => age !== null);
  const guestParty: GuestPartyWritable = { adults: party.adults };
  if (childrenAges.length > 0) guestParty.children_ages = childrenAges;
  if (party.infants > 0) guestParty.infants = party.infants;
  return guestParty;
}

// fromGuestParty converts the wire GuestParty (HoldView/BookingView `guests`,
// Task 6/7) into the picker's PartyValue so partyLabel (lib/party.ts, Task 12)
// can render it — the opposite direction of toGuestParty, but NOT its mirror
// image. For a hold, `children` always equals `children_ages.length` (a hold
// is only ever created from a complete picker party). For a booking it is
// NOT: an OTA booking can carry `children: 1` with zero ages, because Channex
// never sends them (see bookingPartyToAPI, internal/api/bookingengine). Since
// partyLabel counts children via `childrenAges.length`, trusting
// `children_ages` alone as the count would silently render that guest away.
// `children` — the count — is authoritative; any age beyond what
// `children_ages` supplies becomes `null`, the same "unanswered" convention
// PartyValue already uses for a child added but not yet aged.
export function fromGuestParty(guests: GuestParty): PartyValue {
  const ages = guests.children_ages ?? [];
  const count = guests.children ?? ages.length;
  const childrenAges: (number | null)[] = Array.from({ length: count }, (_, i) => ages[i] ?? null);
  return { adults: guests.adults, childrenAges, infants: guests.infants ?? 0 };
}

export interface QuoteParams {
  apartmentId: string;
  checkin: Date;
  checkout: Date;
  // Absent means the caller states no composition (matches the server's own
  // "absent = no capacity judgement" contract, api/booking-openapi.yaml) —
  // BookingWidget always has a real PartyValue once its picker mounts, so
  // in practice this is always present from that caller.
  guests?: PartyValue;
}

// fetchQuote calls POST /quote. Throws on transport error; returns the Quote
// (which may be available=false) on success.
export async function fetchQuote(params: QuoteParams): Promise<Quote> {
  const { data, error } = await createQuote({
    body: {
      apartment_id: params.apartmentId,
      checkin: toApiDate(params.checkin),
      checkout: toApiDate(params.checkout),
      guests: params.guests ? toGuestParty(params.guests) : undefined,
    },
  });
  if (error || !data) {
    throw new Error('quote_failed');
  }
  return data;
}

export interface ReserveParams extends QuoteParams {
  fullName: string;
  guestEmail: string;
  // E.164, as PhoneField emits it. Required by the server (OH-81) — a direct
  // guest with no reachable number is one operations cannot warn about a late
  // check-in, or reach when nobody answers the door.
  guestPhone: string;
  // The guest ticked the WhatsApp box. False is the normal case and carries
  // no meaning beyond "did not ask for it".
  whatsappOptIn: boolean;
  quotedTotalMinor: number;
  idempotencyKey: string;
}

// ReserveResult is a discriminated union: a created hold, a re-quote on a price
// change, or an unavailable outcome.
export type ReserveResult =
  | { kind: 'created'; hold: Hold }
  | { kind: 'price_changed'; quote: Quote }
  | { kind: 'unavailable'; quote: Quote }
  // message carries a guest-fixable reason when the server named one. Absent
  // means the caller should say something generic — see guestReason.
  | { kind: 'error'; message?: string };

// guestReason turns a 422 problem body into a sentence worth showing a guest,
// or undefined when there is nothing useful to say.
//
// Why this exists: the server answered "hold: validation failed: phone is not
// a valid number" and the page said "Could not reserve. Please try again."
// The one fact the guest needed — that the phone was the problem — was the one
// thing thrown away, at the last step before payment. Someone who typed a
// local number under the wrong country code retries forever, changing nothing,
// because nothing told them what to change.
//
// Only `validation` is surfaced. A 500 or a rate limit is not something the
// guest can fix by reading our internals, so those keep the generic line.
// Showing this text is safe by contract, not by luck: the API's 422 says
// `detail` names the offending field and never echoes the submitted value.
export function guestReason(status: number | undefined, body: unknown): string | undefined {
  if (status !== 422) return undefined;
  if (!body || typeof body !== 'object') return undefined;
  const { code, detail } = body as { code?: string; detail?: string };
  if (code !== 'validation' || typeof detail !== 'string') return undefined;

  // Strip the Go error-wrapping prefixes ("hold: ", "validation failed: ")
  // that accumulate on the way up. They are true and useless to a guest.
  let text = detail.trim();
  for (;;) {
    const next = text.replace(/^(?:validation failed|[a-z][a-z0-9_]*):\s*/i, '');
    if (next === text) break;
    text = next;
  }
  if (!text) return undefined;
  return text.charAt(0).toUpperCase() + text.slice(1) + (/[.!?]$/.test(text) ? '' : '.');
}

// analyticsDistinctID returns this browser's PostHog id, or undefined when
// PostHog never initialised (no key configured, or the guest blocks it).
//
// Consent gates it (OH-72). Without a "yes" there is no persistent identity to
// hand over: before a choice PostHog runs cookieless, so the value would be a
// per-load hash that identifies nothing later, and after a Reject sending one
// at all would put the guest's analytics id in our database on the strength of
// a refusal. The server already drops events with an empty id, so undefined
// here means the hold simply goes unreported — never that it fails.
function analyticsDistinctID(): string | undefined {
  if (!analyticsAllowed()) return undefined;
  try {
    return posthog.get_distinct_id?.() || undefined;
  } catch {
    return undefined;
  }
}

// reserve calls POST /holds. The 409 body (price_changed | unavailable) carries
// the fresh server quote so the UI can re-confirm.
export async function reserve(params: ReserveParams): Promise<ReserveResult> {
  const { data, error, response } = await createHold({
    body: {
      apartment_id: params.apartmentId,
      checkin: toApiDate(params.checkin),
      checkout: toApiDate(params.checkout),
      full_name: params.fullName,
      guest_email: params.guestEmail,
      guest_phone: params.guestPhone,
      whatsapp_opt_in: params.whatsappOptIn,
      quoted_total_minor: params.quotedTotalMinor,
      guests: params.guests ? toGuestParty(params.guests) : undefined,
      // OH-71: hand the server this browser's analytics identity so a hold
      // that later expires unpaid can be reported as the same visitor. By
      // then the browser is gone, and an event under a synthetic id could
      // never join the funnel it completes. Undefined when analytics is off
      // — that must not stop anyone from holding dates.
      analytics_id: analyticsDistinctID(),
    },
    headers: { 'Idempotency-Key': params.idempotencyKey },
  });

  if (data) {
    return { kind: 'created', hold: data };
  }
  // 409: the generated error type is the HoldConflict problem body.
  if (response?.status === 409 && error && typeof error === 'object' && 'code' in error) {
    const body = error as { code?: string; quote?: Quote };
    if (body.code === 'price_changed' && body.quote) {
      return { kind: 'price_changed', quote: body.quote };
    }
    if (body.quote) {
      return { kind: 'unavailable', quote: body.quote };
    }
  }
  return { kind: 'error', message: guestReason(response?.status, error) };
}

// isValidIsoDate reports whether `s` is a real calendar date in YYYY-MM-DD
// form — the same local-date convention as toApiDate/parseIso elsewhere in
// this codebase (never UTC/toISOString). Used at the URL boundary
// (apartment.$id.tsx's validateSearch, OH-64 whole-branch review CRITICAL 2):
// a type-only `typeof s === 'string'` guard let ANY string through to
// DateRangeCalendar's parseIso, which throws on anything that doesn't split
// into three numeric parts — a throw during render that bubbles to the root
// errorComponent and blanks the whole page.
//
// The regex alone is not enough: it accepts a structurally-shaped but
// impossible date like "2026-02-31", because `new Date(y, mo, d)` NORMALIZES
// out-of-range components (Feb 31 → Mar 3) rather than rejecting them. The
// round-trip below — parse, then read the fields back — catches exactly
// that: a normalized date's fields no longer match what was typed.
export function isValidIsoDate(s: string): boolean {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!match) return false;
  const [, yStr, moStr, dStr] = match;
  // noUncheckedIndexedAccess types each capture group as `string | undefined`
  // even though a successful match on this pattern always yields exactly 3
  // groups; the repo lint bans `!`, so narrow explicitly instead.
  if (yStr === undefined || moStr === undefined || dStr === undefined) return false;
  const y = Number(yStr);
  const mo = Number(moStr);
  const d = Number(dStr);
  const parsed = new Date(y, mo - 1, d);
  return parsed.getFullYear() === y && parsed.getMonth() === mo - 1 && parsed.getDate() === d;
}

// isValidEmail is a minimal client-side email check (the server re-validates).
export function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
}

// isValidName is a minimal client-side check: a non-empty name (the server
// re-validates and the Channex CRS needs at least one token for the surname).
export function isValidName(name: string): boolean {
  return name.trim().length > 0;
}

// --- checkout / capture (sub-slice 4A) ---

export interface CheckoutIntent {
  orderId: string;
  approvalUrl: string;
  provider: string;
}

// CheckoutOutcome is a discriminated union returned by startCheckout.
//   ready    — a PayPal order is ready; pass orderId to the button.
//   expired  — the hold expired, or is otherwise no longer payable
//              (410, or 409 with any code OTHER than documents_changed —
//              e.g. hold_not_payable, apartment_unavailable); the guest
//              must start over.
//   documents_changed — 409 documents_changed (OH-69 fix round 1): what
//              the guest ticked no longer matches what the server requires
//              (a republish, or a house-rules edit, mid-dwell). NOT the
//              same as expired — the hold is still fine; only the accepted
//              documents need refetching and a fresh tick.
//   unavailable — checkout is not configured on the server (503).
//   server_error — the server failed while creating the order (5xx other
//              than 503). Distinct from `error` because retrying does not
//              help: the guest is looking at our outage, not a hiccup on the
//              way to us. OH-148 — prod rejected every checkout for hours
//              behind a "please try again" that could never come true.
//   error    — transient failure (no response, transport-level); retry is
//              genuinely worth a shot.
export type CheckoutOutcome =
  | { kind: 'ready'; intent: CheckoutIntent }
  | { kind: 'expired' }
  | { kind: 'documents_changed' }
  | { kind: 'unavailable' }
  | { kind: 'server_error' }
  | { kind: 'error' };

// startCheckout calls POST /holds/{id}/checkout to create (or reuse) a PayPal
// order. It is idempotent server-side: a repeat call returns the same order.
// idempotencyKey is a per-checkout-session UUID (reused across retries).
//
// OH-69: `accepted` carries the guest's checkout-time consent-checkbox
// state and `locale` the BCP-47 locale the checkout UI was actually
// rendering (book.$holdId.tsx passes paraglide's baseLocale — the guest
// site ships English-only today, so that IS the locale shown, not a guess).
// The server refuses with 422 when accepted is not true; the checkbox being
// unticked is what SHOULD reach the server as a refusal, not something this
// function pre-empts client-side — see book.$holdId.tsx's own gate (the pay
// control isn't even rendered until ticked) for the belt this suspenders.
//
// OH-69 fix round 1: `acceptedDocuments` names EXACTLY the (kind, version,
// sha256) the checkout page displayed the checkbox against — never
// recomputed server-side from "whatever is current now". book.$holdId.tsx
// builds this from legalDocumentsQuery's response (terms/cancellation) plus
// a client-side sha256 of the house-rules text (when shown). A mismatch
// (the server independently republished, or an apartment's house rules
// changed, since the page loaded) comes back as 409 documents_changed, not
// a silent accept of the wrong text.
export async function startCheckout(
  holdId: string,
  idempotencyKey: string,
  accepted: boolean,
  acceptedDocuments: AcceptedDocument[],
  locale?: string,
): Promise<CheckoutOutcome> {
  const { data, error, response } = await checkoutHold({
    path: { id: holdId },
    headers: { 'Idempotency-Key': idempotencyKey },
    body: { accepted, accepted_documents: acceptedDocuments, locale },
  });

  if (data) {
    return {
      kind: 'ready',
      intent: { orderId: data.order_id, approvalUrl: data.approval_url, provider: data.provider },
    };
  }
  if (response?.status === 409) {
    const code = error && typeof error === 'object' && 'code' in error ? (error as { code?: string }).code : undefined;
    if (code === 'documents_changed') {
      return { kind: 'documents_changed' };
    }
    return { kind: 'expired' };
  }
  if (response?.status === 410) {
    return { kind: 'expired' };
  }
  if (response?.status === 503) {
    return { kind: 'unavailable' };
  }
  if (response && response.status >= 500) {
    return { kind: 'server_error' };
  }
  void error;
  return { kind: 'error' };
}

// CaptureOutcomeKind mirrors the server CaptureOutcome plus transport states.
// On success the server may return a short-lived confirmationToken; the client
// navigates to /confirmation?token=… to poll for the async-created booking.
export type CaptureOutcomeResult =
  | { kind: 'captured'; confirmationToken?: string } // payment captured, booking finalizing async
  | { kind: 'pending'; confirmationToken?: string } // capture pending (rare); keep waiting
  | { kind: 'expired' }
  | { kind: 'error' };

// captureCheckout calls POST /holds/{id}/capture from the PayPal onApprove
// callback. The booking is created asynchronously, so a success here means
// "payment received, finalizing" — NOT that the booking row exists yet.
export async function captureCheckout(holdId: string, idempotencyKey: string): Promise<CaptureOutcomeResult> {
  const { data, response } = await captureHold({
    path: { id: holdId },
    headers: { 'Idempotency-Key': idempotencyKey },
  });

  if (data) {
    const token = data.confirmation_token;
    return data.status === 'pending'
      ? { kind: 'pending', confirmationToken: token }
      : { kind: 'captured', confirmationToken: token };
  }
  if (response?.status === 410) {
    return { kind: 'expired' };
  }
  return { kind: 'error' };
}

// fetchConfirmation polls GET /confirmation?token=. Returns the booking once the
// async row exists, or null while it is still finalizing (404) — the caller
// retries. Throws only on a non-404 transport error.
export async function fetchConfirmation(token: string): Promise<BookingView | null> {
  const { data, error, response } = await getConfirmation({ query: { token } });
  if (data) return data;
  if (response?.status === 404) return null; // still finalizing / invalid → poll/fallback
  if (error) throw new Error('confirmation_fetch_failed');
  return null;
}

// fetchHold calls GET /holds/{id} (Task 9). Returns the hold's server-frozen
// snapshot — total, currency, dates and its price_breakdown — once it lands,
// so the checkout page (Task 12) survives a reload: today it reads the total
// ONLY from router state, which a reload empties. 404 covers unknown,
// terminal AND expired-but-not-yet-swept holds without distinguishing
// between them (holds.go never discloses which); the checkout page treats
// that the same as "nothing new from the server yet" and keeps whatever it
// already had from location.state. Throws only on a transport/5xx error.
export async function fetchHold(holdId: string): Promise<HoldView | null> {
  const { data, error, response } = await getHold({ path: { id: holdId } });
  // OH-82: the hold's deadline is the server's instant, so the countdown that
  // renders it has to be measured against the server's clock. Every response
  // already carries one in its Date header — this is where we pick it up.
  noteServerDate(response?.headers?.get('date'));
  if (data) return data;
  if (response?.status === 404) return null;
  if (error) throw new Error('hold_fetch_failed');
  return null;
}
